import { useNavigate } from "react-router-dom";
import { useAppDispatch } from "@/services/store";
import { useCalendarEventItem } from "@/entities/calendar-event/model";
import { type AnniversaryEvent, DEFAULT_NOTE_TYPES, EventTypes } from "@/services/api";
import { TableCardMobile } from "@/common/TableCardMobile";
import { ConfettiIcon } from "@/shared/icons/ConfettiIcon";
import { PersonRecord } from "@/common/PersonRecord";
import { appRouterPaths } from "@/services/navigation";
import { mobileNoteEditorSliceActions } from "@/common/NotesTable/model/mobile-note-editor-slice";
import { calendarSliceActions } from "@/features/calendar/model";
import { handleFullName } from "@/shared/utils/handle-contacts-null";
import { useRuntimeImage } from "@/entities/runtime-image";
import type { FC } from "react";
import type { Dayjs } from "dayjs";
import styles from "../../styles.module.scss";
import { getTableCardMobileContent } from "../../../../../../shared/utils/get-mobile-content";

interface Props {
  type: EventTypes;
  id: string;
  currentDate: Dayjs;
  anniversaryRecord?: AnniversaryEvent;
}

export const CalendarEventCard: FC<Props> = ({ type, id, currentDate, anniversaryRecord }) => {
  const { note, anniversary } = useCalendarEventItem(id, type, currentDate);
  const { getRuntimeImageURL } = useRuntimeImage();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const handleClick = () => {
    if (note?.note && note.person) {
      dispatch(
        mobileNoteEditorSliceActions.setNote({
          ...note.note,
          person: note.person,
        }),
      );
      dispatch(mobileNoteEditorSliceActions.setEdit(true));
      navigate(appRouterPaths.commitments);
      dispatch(calendarSliceActions.closeDrawer());
    }
  };

  const getContent = () => {
    switch (type) {
      case EventTypes.NOTE:
        {
          if (note?.note && note.person) {
            return (
              <div onClick={handleClick}>
                <TableCardMobile
                  key={note.note.id}
                  content={getTableCardMobileContent(note)}
                  settings={{
                    isShowFlag: note.note.noteType.name === DEFAULT_NOTE_TYPES.DEFAULT_COMMITMENT,
                    isEdit: false,
                  }}
                  status={note.note.status}
                  noteType={note.note.noteType.name}
                  important={note.note.important ?? false}
                />
              </div>
            );
          }
        }
        return;
      case EventTypes.ANNIVERSARY: {
        return (
          <div
            className={styles["anniversary-items"]}
            onClick={() => navigate(`/contacts/generalInfo/${anniversary?.id}`)}
          >
            <div className={styles["event-header"]}>
              <ConfettiIcon />
              <p className={styles["event-header-text"]}>{anniversaryRecord?.description}</p>
            </div>
            <PersonRecord
              avatar={getRuntimeImageURL({ fileId: anniversary?.avatarFile?.fileId ?? "" })}
              group={anniversary?.group ?? ""}
              name={handleFullName(
                anniversary?.firstName,
                anniversary?.lastName,
                anniversary?.middleName,
              )}
              title={anniversary?.title ?? ""}
              avatarSize={"normal"}
            />
          </div>
        );
      }
    }
  };

  return (
    <li>
      <div className={styles["note-items"]}>{getContent()}</div>
    </li>
  );
};
